import { Course } from '../../../mocks/courses';
import { useNavigate, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { sanitizeRichHtml, stripRichText } from '@/lib/richText';

interface Props {
  course: Course;
}

const CourseHero = ({ course }: Props) => {
  const navigate = useNavigate();
  const richDescription = useMemo(() => sanitizeRichHtml(course.description), [course.description]);
  const shouldShowSubtitle = useMemo(() => {
    const subtitle = (course.subtitle || '').trim();
    const descriptionText = stripRichText(course.description).trim();
    if (!subtitle) {
      return false;
    }
    if (!descriptionText) {
      return true;
    }
    return subtitle !== descriptionText;
  }, [course.subtitle, course.description]);

  return (
    <div className={`bg-gradient-to-br ${course.gradient} relative overflow-hidden`}>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />
      {/* Decorative circles */}
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5" />
      <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-white/5" />

      <div className="relative max-w-7xl mx-auto px-6 py-14">
        <div className="max-w-3xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/60 text-xs mb-5">
            <Link to="/courses" className="hover:text-white transition-colors cursor-pointer">課程中心</Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <span className="text-white/80">{course.category}</span>
            <i className="ri-arrow-right-s-line text-white/40" />
            <span className="text-white truncate max-w-xs">{course.title}</span>
          </div>

          {/* Tags row */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className={`${course.tagColor} text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap`}>
              {course.tag}
            </span>
            <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
              {course.level}
            </span>
            <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap inline-flex items-center gap-1.5">
              <i className={`${course.icon || 'ri-apps-line'} text-xs`} />
              {course.category}
            </span>
            {course.certificate && (
              <span className="bg-amber-400/30 text-amber-200 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                <i className="ri-award-line text-xs" />
                結課證書
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-3 leading-tight">{course.title}</h1>

          {/* Description */}
          <div
            className="course-rich-content text-white/65 text-sm leading-relaxed mb-6 max-w-2xl"
            dangerouslySetInnerHTML={{ __html: richDescription }}
          />

          {/* Stats bar */}
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className={`ri-star-${star <= Math.floor(course.rating) ? 'fill' : star - 0.5 <= course.rating ? 'half-fill' : 'line'} text-amber-400 text-sm`} />
                ))}
              </div>
              <span className="text-amber-300 font-bold text-sm ml-1">{course.rating}</span>
              <span className="text-white/50 text-sm">({course.reviews.toLocaleString()} 則評價)</span>
            </div>
            <div className="flex items-center gap-1.5 text-white/70 text-sm">
              <i className="ri-group-line text-white/50" />
              <span>{course.students.toLocaleString()} 名學員已報名</span>
            </div>
            <div className="flex items-center gap-1.5 text-white/70 text-sm">
              <i className="ri-time-line text-white/50" />
              <span>總時長 {course.duration}</span>
            </div>
            {course.lastUpdated && (
              <div className="flex items-center gap-1.5 text-white/70 text-sm">
                <i className="ri-refresh-line text-white/50" />
                <span>最近更新：{course.lastUpdated}</span>
              </div>
            )}
            {course.language && (
              <div className="flex items-center gap-1.5 text-white/70 text-sm">
                <i className="ri-translate-2 text-white/50" />
                <span>{course.language}</span>
              </div>
            )}
          </div>

          {/* Instructor brief */}
          <div className="mt-6 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
              <i className="ri-user-line text-white/80 text-sm" />
            </div>
            <span className="text-white/70 text-sm">
              主講導師：
              <span className="text-white font-semibold ml-1">{course.instructor}</span>
              <span className="text-white/50 mx-1.5">·</span>
              <span className="text-white/80">{course.instructorCompany}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseHero;
